import {
  Box,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { ShoppingCartOutlined } from "@material-ui/icons";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat/NumberFormat";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { DomesticOrderSortField } from "@saleor/createOrderDomestic/urls";
import { ContractQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { maybe, renderCollection } from "@saleor/misc";
import { SortPage } from "@saleor/types";
import {
  getSalesUnit,
  PROD_CATEG_1,
  SALE_CATEG_1
} from "@saleor/utils/constants";
import { convertToRol } from "@saleor/utils/data/convertUnit";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface TableProductsProps extends SortPage<DomesticOrderSortField> {
  products: ContractQuery["contract"]["products"];
  contract: ContractQuery["contract"];
  onClickButton: (productId, contractMaterialId) => void;
  staffUpdate?: number;
  onAddToCart?: (product) => void;
  isCreateOrderFlow?: boolean;
  isChangeOrderLineUpdatedMat?: boolean;
  remainProductInOrderLinesCateg1?: any;
  remainProductInOrderLines?: any;
  remainChangeOrderCateg1?: any;
  remainChangeOrder?: any;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      border: "1px solid #E0E0E0",
      "& .MuiTableHead-root .MuiTableCell-root": {
        height: "48px !important"
      },
      "& .MuiTableRow-root:before": {
        display: "none"
      }
    },
    cellIndex: {
      maxWidth: 40,
      lineHeight: 22,
      padding: "0 2.4rem !important"
    },
    btnOpen: {
      width: 136,
      height: 32
    },
    btnAddOrder: {
      width: "max-content",
      height: 32
    },
    cellBtn: {
      maxWidth: 150,
      paddingRight: 0
    },
    cellHead: {
      color: theme.palette.grey["600"]
    },
    colGrey: {
      background: `${theme.palette.grey["200"]} !important`
    },
    itemSelect: {
      display: "flex",
      justifyContent: "space-between"
    },
    col100: {
      width: 100
    },
    col150: {
      width: 150
    },
    colMatCode: {
      minWidth: 175
    },
    colButton: {
      width: 170,
      minWidth: 170,
      maxWidth: 170,
      "& button": {
        height: "3.2rem",
        minWidth: "136px"
      }
    },
    cellWithBackground: {
      background: `${theme.palette.grey[200]} !important`,
      paddingLeft: "8px",
      textAlign: "center",
      width: 150,
      "& > div": {
        justifyContent: "center"
      }
    },
    cellWithBackgroundRight: {
      background: `${theme.palette.grey[200]} !important`,
      paddingLeft: "8px",
      textAlign: "right",
      width: 120,
      "& > div": {
        justifyContent: "flex-end"
      }
    },
    lightSpan: {
      fontWeight: 100
    },
    errorText: {
      color: theme.palette.error.light,
      marginTop: 5
    }
  }),
  { name: "TableProductsDraw" }
);

const TableProducts: React.FC<TableProductsProps> = ({
  contract,
  products,
  onClickButton,
  isCreateOrderFlow,
  isChangeOrderLineUpdatedMat,
  remainProductInOrderLinesCateg1 = {},
  remainProductInOrderLines = {},
  remainChangeOrderCateg1 = {},
  remainChangeOrder = {},
  onAddToCart
}) => {
  const intl = useIntl();
  const [productsState, setProductsState] = useState([]);
  const [_reRender, setReRender] = useState<number>(0);
  const classes = useStyles();

  useEffect(() => {
    const listProductDomestic =
      products?.map(item => ({ ...item, quantity: "" })) || [];
    setProductsState(listProductDomestic);
  }, [products]);

  const isMatType8182 = product =>
    product.materialType === "81" || product.materialType === "82";

  const haveRowMatType8182 = () =>
    products.some(product => isMatType8182(product.product));

  const isProductCateg1 = PROD_CATEG_1(contract?.prcGroup1);

  const handleChangeQuantity = (e, productId) => {
    const newProductsState = [...productsState];
    const productChange = newProductsState.find(item => item.id === productId);
    productChange.quantity = e.target.value;

    if (isProductCateg1) {
      if (isChangeOrderLineUpdatedMat || isCreateOrderFlow) {
        productChange.errorQuantity =
          parseFloat(e.target.value) * (productChange.product?.weight || 0) >
          ((remainChangeOrderCateg1[productId] || productChange.remain) -
            (remainProductInOrderLinesCateg1[productId] || 0) || 0);
      } else {
        productChange.errorQuantity =
          parseFloat(e.target.value) * (productChange.product?.weight || 0) >
          (remainChangeOrderCateg1[productId] || productChange.remain);
      }
    } else {
      if (isChangeOrderLineUpdatedMat || isCreateOrderFlow) {
        const newQtys = Number(e.target.value);
        const remainCalc =
          (remainChangeOrder[productId] || productChange.remain) -
            (remainProductInOrderLines[productId] || 0) || 0;
        const remainCalcRound = Number(remainCalc.toFixed(3));
        productChange.errorQuantity = newQtys > remainCalcRound;
      } else {
        const newQtys = Number(e.target.value);
        const remainCalc = productChange.remain;
        const remainCalcRound = Number(remainCalc.toFixed(3));
        productChange.errorQuantity = newQtys > remainCalcRound;
      }
    }

    setProductsState(newProductsState);
    setReRender(prev => prev + 1);
  };

  const getQuantityProduct = productId => {
    const product = productsState.find(item => item.id === productId);
    return product?.quantity || "";
  };

  const getErrorProduct = productId => {
    const product = productsState.find(item => item.id === productId);
    return !!product?.errorQuantity;
  };

  const handleAddToCart = product => {
    const productAdd = productsState.find(item => item.id === product.id);
    onAddToCart(productAdd);
    handleChangeQuantity({ target: { value: 0 } }, product.id);
  };

  return (
    <div className={classes.root}>
      <ResponsiveTable>
        <TableHead>
          <TableCellHeader className={classes.col100}>
            <MyTypography variant="subtitle3" className={classes.cellHead}>
              {intl.formatMessage({ defaultMessage: "ลำดับ" })}
            </MyTypography>
          </TableCellHeader>
          <TableCellHeader className={classes.colMatCode}>
            <MyTypography variant="subtitle3" className={classes.cellHead}>
              {intl.formatMessage({ defaultMessage: "Material Code" })}
            </MyTypography>
          </TableCellHeader>
          <TableCellHeader>
            <MyTypography variant="subtitle3" className={classes.cellHead}>
              {intl.formatMessage({ defaultMessage: "ช่ือสินค้า" })}
            </MyTypography>
          </TableCellHeader>
          <TableCellHeader className={classes.col150}>
            <MyTypography variant="subtitle3" className={classes.cellHead}>
              {haveRowMatType8182() ? (
                <FormattedMessage defaultMessage="จำนวนสั่ง" />
              ) : (
                ""
              )}
            </MyTypography>
          </TableCellHeader>
          <TableCellHeader className={classes.col100}>
            <MyTypography variant="subtitle3" className={classes.cellHead}>
              {haveRowMatType8182() ? (
                <FormattedMessage defaultMessage="หน่วย" />
              ) : (
                ""
              )}
            </MyTypography>
          </TableCellHeader>
          <TableCellHeader className={classes.colButton} />
          <TableCellHeader className={classes.cellWithBackgroundRight}>
            <FormattedMessage defaultMessage="จำนวน" />
          </TableCellHeader>
          <TableCellHeader className={classes.cellWithBackgroundRight}>
            <FormattedMessage defaultMessage="คงเหลือ" />
          </TableCellHeader>
          <TableCellHeader className={classes.cellWithBackground}>
            <FormattedMessage defaultMessage="หน่วย" />
          </TableCellHeader>
          <TableCellHeader className={classes.cellWithBackgroundRight}>
            <FormattedMessage defaultMessage="ราคา" />
            <span className={classes.lightSpan}>({contract?.currency})</span>
            <br />
            <FormattedMessage defaultMessage="ต่อหน่วยขาย" />
          </TableCellHeader>
        </TableHead>
        <TableBody>
          {products &&
            renderCollection(productsState, (product, index) => (
              <TableRow key={product?.id}>
                <TableCell>
                  <MyTypography variant="body3">{index + 1}</MyTypography>
                </TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  <MyTypography variant="body3">
                    <Box className={classes.itemSelect}>
                      {maybe(() => product?.materialCode)}
                    </Box>
                  </MyTypography>
                </TableCell>
                <TableCell>
                  <MyTypography variant="body3">
                    <Box className={classes.itemSelect}>
                      {maybe(() => product.product?.description)}
                    </Box>
                  </MyTypography>
                </TableCell>
                <TableCell>
                  {isMatType8182(product.product) ? (
                    <NumberFormat
                      id={`lines[${index}].quantity`}
                      onChange={e => handleChangeQuantity(e, product.id)}
                      value={getQuantityProduct(product.id)}
                      allowLeadingZeros={
                        !SALE_CATEG_1(
                          getSalesUnit(product?.salesUnit, product?.matGroup1)
                        )
                      }
                      decimalScale={
                        SALE_CATEG_1(
                          getSalesUnit(product?.salesUnit, product?.matGroup1)
                        )
                          ? 3
                          : 0
                      }
                      error={getErrorProduct(product.id)}
                      placeholder={
                        SALE_CATEG_1(
                          getSalesUnit(product?.salesUnit, product?.matGroup1)
                        )
                          ? "0.000"
                          : "0"
                      }
                      rightAlignPlaceholder={SALE_CATEG_1(
                        getSalesUnit(product?.salesUnit, product?.matGroup1)
                      )}
                      fixedDecimalScale={SALE_CATEG_1(
                        getSalesUnit(product?.salesUnit, product?.matGroup1)
                      )}
                    />
                  ) : (
                    ""
                  )}
                  {getErrorProduct(product.id) ? (
                    <div className={classes.errorText}>
                      {intl.formatMessage({
                        defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
                      })}
                    </div>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell data-testid="salesUnit-cell">
                  {isMatType8182(product.product) ? (
                    product.matGroup1 === "K01" ||
                    product.matGroup1 === "K09" ? (
                      <FormattedMessage defaultMessage="ม้วน" />
                    ) : (
                      <span>{product.salesUnit}</span>
                    )
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell className={classes.colButton}>
                  {isMatType8182(product.product) ? (
                    <Button
                      id={`lines[${index}].addToOrderButton`}
                      className={classes.btnAddOrder}
                      variant="contained"
                      color="secondary"
                      startIcon={<ShoppingCartOutlined />}
                      onClick={() => handleAddToCart(product)}
                      disabled={getErrorProduct(product.id)}
                    >
                      {intl.formatMessage({
                        defaultMessage: "Add to order"
                      })}
                    </Button>
                  ) : (
                    <Button
                      id={"lines[" + index + "].selectItemButton"}
                      data-testid={"lines[" + index + "].selectItemButton"}
                      color="secondary"
                      variant="contained"
                      className={classes.btnOpen}
                      onClick={() =>
                        onClickButton(product.productId, product.id)
                      }
                    >
                      <MyTypography variant="subtitle3" color="inherit">
                        เลือกสินค้า
                      </MyTypography>
                    </Button>
                  )}
                </TableCell>
                <TableCell className={classes.cellWithBackgroundRight}>
                  {product ? (
                    <MyTypography variant="body3">
                      <DecimalFormat
                        value={product.total}
                        isGrayDecimal
                        countDecimal={3}
                      />
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.cellWithBackgroundRight}>
                  {product ? (
                    <MyTypography variant="body3">
                      <DecimalFormat
                        value={product.remain}
                        isGrayDecimal
                        countDecimal={3}
                      />
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.cellWithBackground}>
                  {product ? (
                    <MyTypography variant="body3">
                      {convertToRol(product.weightUnit)}
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.cellWithBackground}>
                  {product ? (
                    <MyTypography variant="body3">
                      <DecimalFormat value={product.price} />
                    </MyTypography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </ResponsiveTable>
    </div>
  );
};

export default TableProducts;
